import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { BrandMark } from '../components/BrandMark.tsx';
import { Icon } from '../components/Icon.tsx';
import { LanguageSwitch } from '../components/LanguageSwitch.tsx';
import { QrCode } from '../components/Receipt.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, Field, Skeleton, StatusBadge } from '../components/ui.tsx';
import { t, tn } from '../i18n/index.ts';
import { ApiError, request, saveBlob } from '../lib/api.ts';
import { dateTime } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';
import { ConnectedApps } from './DeveloperApps.tsx';

/** What GET /api/security answers. */
interface Status {
  required: boolean;
  password: boolean;
  password_changed_at: string | null;
  authenticator: boolean;
  recovery_codes_left: number;
  remembered_devices: number;
  confirmed: boolean;
  until: string | null;
  can_set_up: boolean;
}
interface Setup {
  secret: string;
  uri: string;
  qr: string[];
}

const message = (err: unknown) => (err instanceof ApiError ? (err.code === 'offline' ? t('error.offline') : err.message) : t('error.generic'));
const field = (err: unknown) => (err instanceof ApiError ? (err.details?.field as string | undefined) : undefined);

/**
 * Settings → Security: the person's own sign-in. Step 1 a password, step 2 an authenticator app,
 * then recovery codes (shown once) and remembered devices. The same panel is the whole screen for
 * someone whose days to set it up have passed.
 */
function SecurityPanel({ onReady }: { onReady?: () => void }) {
  const { refresh } = useSession();
  const [status, setStatus] = useState<Status | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const load = useCallback(
    () =>
      request<Status>('GET', '/api/security').then(
        (s) => {
          setStatus(s);
          return s;
        },
        (err) => {
          setError(message(err));
          return null;
        },
      ),
    [],
  );
  useEffect(() => {
    void load();
  }, [load]);

  const changed = async () => {
    const s = await load();
    await refresh().catch(() => undefined);
    if (s?.password && s.authenticator) onReady?.();
  };

  if (error) {
    return (
      <p className="form-error" role="alert">
        <Icon name="alert" size={16} /> {error}
      </p>
    );
  }
  if (!status) return <Skeleton h={320} r="var(--radius-lg)" />;
  const on = status.password && status.authenticator;
  return (
    <div className="sec-panel">
      <section className="card sec-summary" aria-labelledby="sec-summary">
        <div className="card-head">
          <h2 id="sec-summary">
            <Icon name="shieldCheck" size={18} /> {t('sec.two_step')}
          </h2>
          {on ? (
            <StatusBadge tone="ok" icon="checkCircle" label={t('sec.on')} />
          ) : (
            <StatusBadge tone={status.required ? 'warn' : 'neutral'} icon={status.required ? 'alert' : 'circle'} label={t('sec.off')} />
          )}
        </div>
        <p className="muted">{t(status.required ? 'sec.required_body' : 'sec.optional_body')}</p>
        {!on && status.until ? <p className="sec-until">{t('sec.until', { date: dateTime(status.until) })}</p> : null}
      </section>

      <PasswordCard status={status} onDone={changed} />
      {status.authenticator ? (
        <AppOnCard status={status} onCodes={setCodes} onChange={changed} />
      ) : (
        <AppSetupCard status={status} onCodes={(c) => setCodes(c)} onDone={changed} />
      )}

      {codes ? <RecoveryCodes codes={codes} onClose={() => setCodes(null)} /> : null}
    </div>
  );
}

function StepHead({ n, title, done }: { n: number; title: string; done: boolean }) {
  return (
    <div className="card-head">
      <h2 className="sec-step">
        <span className={`sec-step-n${done ? ' done' : ''}`} aria-hidden="true">
          {done ? <Icon name="check" size={14} /> : n}
        </span>
        {title}
      </h2>
      {done ? <StatusBadge tone="ok" icon="checkCircle" label={t('sec.done')} /> : null}
    </div>
  );
}

function PasswordCard({ status, onDone }: { status: Status; onDone: () => Promise<void> }) {
  const { toast } = useUi();
  const [open, setOpen] = useState(!status.password);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [errors, setErrors] = useState<{ current?: string; next?: string; again?: string; form?: string }>({});
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const errs: typeof errors = {};
    if (!current) errs.current = t(status.password ? 'sec.current_missing' : 'sec.pin_missing');
    if (next.length < 12) errs.next = t('sec.too_short');
    else if (next !== again) errs.again = t('sec.not_same');
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      await request('POST', '/api/security/password', { current, password: next });
      setCurrent('');
      setNext('');
      setAgain('');
      setOpen(false);
      toast(t('sec.password_saved'));
      await onDone();
    } catch (err) {
      const f = field(err);
      setErrors(f === 'current' ? { current: message(err) } : f === 'password' ? { next: message(err) } : { form: message(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card sec-card" aria-label={t('sec.password_title')}>
      <StepHead n={1} title={t('sec.password_title')} done={status.password} />
      {!open ? (
        <div className="sec-row">
          <p className="muted">{status.password_changed_at ? t('sec.password_changed', { date: dateTime(status.password_changed_at) }) : t('sec.password_set')}</p>
          <Button variant="secondary" icon="key" onClick={() => setOpen(true)}>
            {t('sec.password_change')}
          </Button>
        </div>
      ) : (
        <form className="sec-form" onSubmit={submit} noValidate>
          <p className="muted">{t('sec.password_intro')}</p>
          <Field label={t(status.password ? 'sec.current_password' : 'sec.current_pin')} error={errors.current}>
            <input
              className="input"
              type="password"
              autoComplete="current-password"
              inputMode={status.password ? undefined : 'numeric'}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          </Field>
          <Field label={t('sec.new_password')} hint={t('sec.new_password_hint')} error={errors.next} counter={`${next.length}/12+`}>
            <input className="input" type="password" autoComplete="new-password" maxLength={200} value={next} onChange={(e) => setNext(e.target.value)} />
          </Field>
          <Field label={t('sec.new_password_again')} error={errors.again}>
            <input className="input" type="password" autoComplete="new-password" maxLength={200} value={again} onChange={(e) => setAgain(e.target.value)} />
          </Field>
          {errors.form ? (
            <p className="form-error" role="alert">
              <Icon name="alert" size={16} /> {errors.form}
            </p>
          ) : null}
          <div className="form-actions">
            {status.password ? (
              <Button variant="ghost" onClick={() => setOpen(false)}>
                {t('common.cancel')}
              </Button>
            ) : null}
            <Button variant="primary" type="submit" busy={busy}>
              {t('sec.password_save')}
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}

function AppSetupCard({ status, onCodes, onDone }: { status: Status; onCodes: (c: string[]) => void; onDone: () => Promise<void> }) {
  const [setup, setSetup] = useState<Setup | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const codeRef = useRef<HTMLInputElement>(null);

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      setSetup(await request<Setup>('POST', '/api/security/authenticator'));
      setTimeout(() => codeRef.current?.focus(), 0);
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };
  const confirm = async (e: FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) return setError(t('login.code_missing'));
    setBusy(true);
    setError(null);
    try {
      const r = await request<{ recovery_codes: string[] }>('POST', '/api/security/authenticator/confirm', { code });
      onCodes(r.recovery_codes);
      setSetup(null);
      await onDone();
    } catch (err) {
      setCode('');
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card sec-card" aria-label={t('sec.app_title')}>
      <StepHead n={2} title={t('sec.app_title')} done={false} />
      {!status.can_set_up ? (
        <p className="form-error" role="alert">
          <Icon name="alert" size={16} /> {t('sec.no_key')}
        </p>
      ) : !setup ? (
        <div className="sec-row">
          <p className="muted">{t('sec.app_intro')}</p>
          <Button variant="primary" icon="qr" busy={busy} onClick={() => void start()}>
            {t('sec.app_start')}
          </Button>
        </div>
      ) : (
        <form className="sec-app" onSubmit={confirm} noValidate>
          <ol className="pa-steps">
            <li>{t('sec.app_step1')}</li>
            <li>{t('sec.app_step2')}</li>
            <li>{t('sec.app_step3')}</li>
          </ol>
          <div className="sec-app-grid">
            <div className="pa-qr">
              <QrCode rows={setup.qr} label={t('platform.enrol_qr')} />
            </div>
            <div className="sec-app-side">
              <details className="pa-secret">
                <summary>{t('platform.enrol_manual')}</summary>
                <code className="mono">{setup.secret.replace(/(.{4})/g, '$1 ').trim()}</code>
              </details>
              <Field label={t('login.code')}>
                <input
                  ref={codeRef}
                  className="input code-input"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => {
                    setError(null);
                    setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                  }}
                />
              </Field>
              {error ? (
                <p className="form-error" role="alert">
                  <Icon name="alert" size={16} /> {error}
                </p>
              ) : null}
              <Button variant="primary" type="submit" busy={busy} icon="shieldCheck">
                {t('sec.app_finish')}
              </Button>
            </div>
          </div>
        </form>
      )}
      {error && !setup ? (
        <p className="form-error" role="alert">
          <Icon name="alert" size={16} /> {error}
        </p>
      ) : null}
    </section>
  );
}

function AppOnCard({ status, onCodes, onChange }: { status: Status; onCodes: (c: string[]) => void; onChange: () => Promise<void> }) {
  const { toast, fail } = useUi();
  const [asking, setAsking] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const renew = async (e: FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) return setError(t('login.code_missing'));
    setBusy(true);
    try {
      const r = await request<{ recovery_codes: string[] }>('POST', '/api/security/recovery-codes', { code });
      setAsking(false);
      setCode('');
      onCodes(r.recovery_codes);
      await onChange();
    } catch (err) {
      setCode('');
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };
  const forget = async () => {
    try {
      const r = await request<{ forgotten: number }>('POST', '/api/security/forget-devices');
      toast(tn('sec.forgotten', r.forgotten));
      await onChange();
    } catch (err) {
      fail(err);
    }
  };
  return (
    <section className="card sec-card" aria-label={t('sec.app_title')}>
      <StepHead n={2} title={t('sec.app_title')} done />
      <ul className="sec-facts">
        <li>
          <Icon name="key" size={16} />
          <span>{tn('sec.codes_left', status.recovery_codes_left)}</span>
          <Button size="sm" variant="secondary" onClick={() => setAsking(true)}>
            {t('sec.codes_new')}
          </Button>
        </li>
        <li>
          <Icon name="phone" size={16} />
          <span>{tn('sec.devices', status.remembered_devices)}</span>
          {status.remembered_devices ? (
            <Button size="sm" variant="secondary" onClick={() => void forget()}>
              {t('sec.devices_forget')}
            </Button>
          ) : null}
        </li>
      </ul>
      {status.recovery_codes_left <= 3 ? (
        <p className="form-note" role="status">
          <Icon name="alert" size={16} /> {t('sec.codes_low')}
        </p>
      ) : null}
      {asking ? (
        <Dialog title={t('sec.codes_new')} onClose={() => setAsking(false)}>
          <form onSubmit={renew} noValidate className="sec-form">
            <p className="muted">{t('sec.codes_new_body')}</p>
            <Field label={t('login.code')} error={error}>
              <input
                className="input code-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                value={code}
                onChange={(e) => {
                  setError(null);
                  setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                }}
              />
            </Field>
            <div className="form-actions">
              <Button variant="ghost" onClick={() => setAsking(false)}>
                {t('common.cancel')}
              </Button>
              <Button variant="primary" type="submit" busy={busy}>
                {t('sec.codes_make')}
              </Button>
            </div>
          </form>
        </Dialog>
      ) : null}
    </section>
  );
}

/** The recovery codes, shown once: copy, download, and say they are kept somewhere safe. */
function RecoveryCodes({ codes, onClose }: { codes: string[]; onClose: () => void }) {
  const { toast } = useUi();
  const [kept, setKept] = useState(false);
  const text = codes.join('\n');
  return (
    <Dialog title={t('sec.codes_title')} onClose={() => kept && onClose()}>
      <p>{t('sec.codes_body')}</p>
      <ol className="sec-codes mono">
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ol>
      <div className="sec-codes-actions">
        <Button
          variant="secondary"
          icon="copy"
          onClick={() =>
            void navigator.clipboard?.writeText(text).then(
              () => toast(t('sec.copied')),
              () => undefined,
            )
          }
        >
          {t('sec.copy')}
        </Button>
        <Button variant="secondary" icon="download" onClick={() => void saveBlob(new Blob([`ERP PRO\n${text}\n`], { type: 'text/plain' }), 'erp-pro-recovery-codes.txt')}>
          {t('sec.download')}
        </Button>
      </div>
      <label className="check-row">
        <input type="checkbox" checked={kept} onChange={(e) => setKept(e.target.checked)} />
        <span>{t('sec.codes_kept')}</span>
      </label>
      <div className="form-actions">
        <Button variant="primary" disabled={!kept} onClick={onClose}>
          {t('sec.codes_done')}
        </Button>
      </div>
    </Dialog>
  );
}

/** Settings → Security, for everyone signed in. */
export function Security() {
  return (
    <Shell>
      <div className="page sec-page">
        <div className="page-head">
          <div>
            <h1>{t('sec.title')}</h1>
            <p className="muted">{t('sec.subtitle')}</p>
          </div>
        </div>
        <SecurityPanel />
        <ConnectedApps />
      </div>
    </Shell>
  );
}

/** The whole screen for someone whose days to set up the two steps have passed. */
export function SecuritySetup() {
  const { me, signOut } = useSession();
  return (
    <main className="sec-gate">
      <div className="sec-gate-inner">
        <div className="login-head">
          <span className="brand-mark">
            <span className="brand-logo cube">
              <BrandMark size={30} />
            </span>
            <span className="brand-name">{t('app.name')}</span>
          </span>
          <LanguageSwitch />
        </div>
        <h1>{t('sec.gate_title')}</h1>
        <p className="muted">{t('sec.gate_body', { name: me?.name ?? '' })}</p>
        <SecurityPanel />
        <Button variant="ghost" icon="logout" onClick={() => void signOut()}>
          {t('nav.sign_out')}
        </Button>
      </div>
    </main>
  );
}

/** A line at the top while the days to set it up run: who, by when, and the way there. */
export function SecurityBanner() {
  const { me } = useSession();
  const s = me?.security;
  if (!s || s.blocked) return null;
  return (
    <div className="plan-banner plan-banner-warn" role="status">
      <Icon name="shieldCheck" size={18} />
      <span>{t('sec.banner', { date: dateTime(s.until) })}</span>
      <Link className="btn btn-sm btn-primary" to="/security">
        {t('sec.banner_go')}
      </Link>
    </div>
  );
}
